import { Box, Flex } from '@radix-ui/themes';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { EditHitterDialog } from 'components/common/dialogs/edit-hitter';
import { ErrorBoundary } from 'components/common/error-boundary';
import { EditSessionDialog } from 'components/common/sessions/dialogs/edit-session';
import { VisualizeSessionDialog } from 'components/common/sessions/dialogs/visualize-session';
import { GameStatusBanner } from 'components/main/game-status-banner';
import { SectionRouter } from 'components/main/section-router';
import { HittersContext } from 'contexts/hitters.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { SessionDialogMode } from 'enums/session.enums';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

export const Content = () => {
  // need to render these dialogs here so they have access to all the necessary contexts
  const {
    dialogCRUD,
    closeCrudDialog,
    setActive,
    deleteHitters,
    setLastChanged,
  } = useContext(HittersContext);

  const { dialogConfig, fired, sessions, fetchSessions, setDialogConfig } =
    useContext(SessionEventsContext);

  const current = useAuthStore((s) => s.current);

  return (
    <ErrorBoundary identifier="MainContent">
      <Flex
        className={RADIX.VFLEX.WRAPPER}
        justify="center"
        p={RADIX.FLEX.PAD.MAIN}
        // the sidebar's padding already accounts for this side
        pl="0"
      >
        <Flex
          flexGrow="1"
          maxWidth="1700px"
          direction="column"
          className={RADIX.VFLEX.WRAPPER}
          gap={RADIX.FLEX.GAP.MD}
        >
          <GameStatusBanner />

          <Box
            className="MainContentWrapper"
            flexGrow="1"
            overflowY="auto"
            overflowX="hidden"
          >
            <SectionRouter />
          </Box>
        </Flex>
      </Flex>

      {dialogCRUD?.action === CrudAction.Create && (
        <EditHitterDialog
          key={dialogCRUD.key}
          hitter_id={undefined}
          onCreate={(value) => setActive(value._id)}
          onClose={() => {
            dialogCRUD?.onClose?.();
            closeCrudDialog();
            setLastChanged(Date.now());
          }}
        />
      )}

      {dialogCRUD?.action === CrudAction.Update &&
        dialogCRUD.models.length > 0 && (
          <EditHitterDialog
            key={dialogCRUD.key}
            hitter_id={dialogCRUD.models[0]._id}
            onClose={() => {
              dialogCRUD.onClose?.();
              closeCrudDialog();
              setLastChanged(Date.now());
            }}
          />
        )}

      {dialogCRUD?.action === CrudAction.Delete && (
        <CommonConfirmationDialog
          identifier="HittersContextConfirmDelete"
          title={t('common.delete-x', {
            x: t(
              dialogCRUD.models.length === 1
                ? 'hitters.hitter'
                : 'hitters.hitters'
            ),
          }).toString()}
          content={
            <Box>
              <p>
                {t('common.confirm-remove-n-x', {
                  n: dialogCRUD.models.length,
                  x: t(
                    dialogCRUD.models.length === 1
                      ? 'hitters.hitter'
                      : 'hitters.hitters'
                  ).toLowerCase(),
                })}
              </p>
              <ul>
                {dialogCRUD.models.map((v) => (
                  <li key={`del-hitter-${v._id}`}>{v.name}</li>
                ))}
              </ul>
            </Box>
          }
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            invisible: dialogCRUD.models.length === 0,
            onClick: async () => {
              const ids = dialogCRUD.models.map((m) => m._id);
              await deleteHitters(ids);
            },
          }}
        />
      )}

      {dialogConfig?.mode === SessionDialogMode.edit && (
        <EditSessionDialog
          key={dialogConfig.key}
          identifier="EditSessionDialog"
          session={dialogConfig.session}
          fires={
            current.session === dialogConfig.session.sessionID
              ? fired
              : (sessions.find(
                  (s) => s.sessionID === dialogConfig.session.sessionID
                )?.totalFires ?? 0)
          }
          onChanged={fetchSessions}
          onClose={() => setDialogConfig(undefined)}
        />
      )}

      {dialogConfig?.mode === SessionDialogMode.visualize && (
        <VisualizeSessionDialog
          key={dialogConfig.key}
          identifier="VisualizeSessionDialog"
          session={dialogConfig.session}
          onClose={() => setDialogConfig(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
